// Edit one library page — all archetypes use the sandbox block editor.

import { useEffect, useState } from "react";
import { PROPOSAL_TEXT_MAX_LEN } from "@/lib/opportunity/proposalTextSections";
import type { ProposalPage, ProposalPageBody } from "@/lib/opportunity/proposalPageTypes";
import { sandboxBlocksFromPageBody } from "@/lib/opportunity/proposalPageTypes";
import {
  coverPartsFromSandboxBlocks,
  introFooterFromSandboxBlocks,
  textPartsFromSandboxBlocks,
  addableSandboxBlocksForArchetype,
} from "@/lib/opportunity/proposalSandboxStarters";
import type { ProposalSandboxBlock } from "@/lib/opportunity/proposalSandboxBlocks";
import { ProposalSandboxBlockEditor } from "./ProposalSandboxBlockEditor";

type Props = {
  page: ProposalPage;
  onSave: (body: ProposalPageBody, label: string) => Promise<{ ok: boolean; error?: string }>;
  onPreview?: (pageId: string) => void;
};

export function ProposalPageBodyEditor({ page, onSave, onPreview }: Props) {
  const [label, setLabel] = useState(page.label);
  const [blocks, setBlocks] = useState<ProposalSandboxBlock[]>(() =>
    sandboxBlocksFromPageBody(page.archetype, page.body),
  );
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    setLabel(page.label);
    setBlocks(sandboxBlocksFromPageBody(page.archetype, page.body));
    setMsg(null);
  }, [page.id, page.label, page.body, page.archetype]);

  function buildBody(): ProposalPageBody {
    switch (page.archetype) {
      case "cover_letter": {
        const parts = coverPartsFromSandboxBlocks(blocks);
        return { body: parts.body, blocks };
      }
      case "cost_breakdown": {
        const parts = introFooterFromSandboxBlocks(blocks);
        return { intro: parts.intro, footer: parts.footer, blocks };
      }
      case "payment_schedule": {
        const parts = introFooterFromSandboxBlocks(blocks);
        return { intro: parts.intro, footer: parts.footer, blocks };
      }
      case "intent_to_purchase": {
        const parts = textPartsFromSandboxBlocks(blocks);
        return {
          preamble: parts.preamble,
          signature_block: parts.signature_block,
          blocks,
        };
      }
    }
  }

  async function persist(): Promise<{ ok: boolean; error?: string }> {
    for (const b of blocks) {
      if (b.type === "rich_text" && b.text.length > PROPOSAL_TEXT_MAX_LEN) {
        const error = `A text block exceeds ${PROPOSAL_TEXT_MAX_LEN} characters.`;
        setMsg(error);
        return { ok: false, error };
      }
    }
    setSaving(true);
    setMsg(null);
    const r = await onSave(buildBody(), label.trim() || page.label);
    setSaving(false);
    setMsg(r.ok ? "Saved." : r.error || "Save failed.");
    return r;
  }

  return (
    <div className="space-y-3">
      <label className="block text-xs">
        <span className="text-gray-500 dark:text-zinc-400">
          Page label (used as the PDF section title)
        </span>
        <input
          type="text"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          className="mt-1 w-full rounded border border-gray-200 px-2 py-1.5 text-sm dark:border-zinc-700 dark:bg-zinc-800"
        />
      </label>

      <ProposalSandboxBlockEditor
        blocks={blocks}
        onChange={setBlocks}
        addableTypes={addableSandboxBlocksForArchetype(page.archetype)}
      />

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          disabled={saving}
          onClick={() => void persist()}
          className="rounded bg-indigo-600 px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save page"}
        </button>
        {onPreview ? (
          <button
            type="button"
            disabled={saving}
            onClick={() =>
              void persist().then((r) => {
                if (r.ok) onPreview(page.id);
              })
            }
            className="rounded border border-indigo-300 px-3 py-1.5 text-xs font-medium text-indigo-700 hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40"
          >
            Preview
          </button>
        ) : null}
        {msg ? (
          <span className="text-xs text-gray-600 dark:text-zinc-400">{msg}</span>
        ) : null}
      </div>
    </div>
  );
}
